import { Component, OnInit, OnDestroy, HostListener, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ConfirmService } from '@luzula/luzula-lib';
import { ListenService, BodensteckbriefEntry, BodensteckbriefSpalte, BodensteckbriefFbfRow, BodensteckbriefSection } from './listen.service';
import { DataService } from '../data.service';
import { NamensmatrixService } from './namensmatrix.service';

/** Spalten-Tokens, die NICHT zu Schraffur-Spalten gehoeren - das sind die
 *  fixen Tabellen-Spalten (Lokalname, Abk., NKS, 5tel, Forstbetrieb), die in
 *  der Kreuztabelle eigene Bereiche bekommen. */
const NON_SCHRAF_TOKENS = [
  'lokalname', 'abkürzung', 'abkuerzung',
  'nks', 'nährkraftstufe', 'naehrkraftstufe',
  'fünftelstufe', 'fuenftelstufe', '5tel',
  'forstbetrieb', 'taufprofil',
];

/** Reihenfolge des Schraffur-Klick-Zyklus. Werte ausserhalb dieses Sets sind
 *  Freitext (z.B. 'M', 'II, IV') und werden separat behandelt. */
const SCHRAF_NEXT: Record<string, string> = { '': 'haken', 'haken': 'x', 'x': '?', '?': '' };
const SCHRAF_CYCLE = new Set(['', 'haken', 'x', '?']);

/** Sentinel-Route-ID fuer die interaktive Neuanlage. Echte Steckbrief-IDs haben
 *  immer ein '#' (Format "<pdf>#<page>"), kollidieren also nie mit diesem Wert. */
const NEW_ENTRY_PARAM = 'neu';

@Component({
  standalone: false,
  selector: 'app-bodensteckbrief-editor',
  templateUrl: './bodensteckbrief-editor.component.html',
  styleUrls: ['./bodensteckbrief-editor.component.css'],
})
export class BodensteckbriefEditorComponent implements OnInit, OnDestroy {
  private listenService = inject(ListenService);
  private dataService = inject(DataService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private confirmService = inject(ConfirmService);
  private namensmatrixService = inject(NamensmatrixService);

  /** Originaler Steckbrief aus der Liste (Read-only-Referenz fuer Discard). */
  original: BodensteckbriefEntry | null = null;
  /** Editierbare Tiefkopie. Alle Edits gehen hier rein, beim Save wird in der
   *  Liste an gleicher ID ueberschrieben. */
  draft: BodensteckbriefEntry | null = null;
  /** Die ganze Liste muss gehalten werden, damit beim Save nicht nur der eine
   *  Eintrag, sondern die volle Liste zurueckgeschrieben werden kann (so
   *  funktioniert BaseListenService - 1 Dokument pro Liste). */
  private fullList: BodensteckbriefEntry[] = [];

  /** Route-ID des bearbeiteten Steckbriefs - wird beim Zurueck mitgegeben,
   *  damit die Listenverwaltung wieder auf genau diesen Eintrag zentriert. */
  private entryId = '';

  /** Alle Steckbrief-IDs in Katalog-Reihenfolge (Seitenzahl aufsteigend, dann
   *  HBF-Abkuerzung, dann Name) - Quelle fuer das Vor/Zurueck-Blaettern. Gleiche
   *  Reihenfolge wie der Katalog-Reader und die Listen-Uebersicht (Default). */
  orderedIds: string[] = [];

  /** Abo auf paramMap, damit Blaettern (gleiche Route, neue :id) den naechsten
   *  Steckbrief neu laedt - der Router verwendet die Komponente wieder. */
  private routeSub?: Subscription;

  loading = true;
  notFound = false;
  /** true, solange dieser Editor einen noch nicht persistierten Steckbrief
   *  anlegt (Route .../steckbrief/neu/edit). Die ID wird erst beim ersten
   *  Speichern vergeben. */
  isNew = false;
  dirty = false;
  saving = false;
  /** Live-Preview als zweite Spalte einblenden. */
  showPreview = false;
  /** Original-PDF-Seite (vom luzula-api) dazu blenden - On-Demand. */
  showOriginal = false;

  /** Sortierte IDs der Schraffur-Spalten (gefiltert nach _is_skipcol-Logik). */
  schrafCols: string[] = [];
  /** Anzahl Header-Ebenen-Zeilen, die der Editor anzeigt (mind. 2). */
  nEbenen = 2;
  /** Wird true wenn mindestens eine FBF eine footnote_ref hat. */
  hasFootnoteCol = false;

  async ngOnInit(): Promise<void> {
    const list = (await this.listenService.getList('Bodensteckbriefe')) as BodensteckbriefEntry[] | null;
    this.fullList = list || [];
    this.rebuildOrderedIds();
    // Auf :id-Wechsel reagieren: Vor/Zurueck-Blaettern navigiert auf dieselbe
    // Route mit neuer :id; der Router verwendet die Komponente wieder und feuert
    // hier - so laedt der naechste Steckbrief, ohne dass ngOnInit erneut laeuft.
    this.routeSub = this.route.paramMap.subscribe(pm => {
      this.loadEntry(pm.get('id') || '');
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  /** Laedt den Steckbrief zur gegebenen Route-ID in den Editor. Wird beim ersten
   *  Aufruf und bei jedem Blaettern (paramMap-Abo) ausgefuehrt. Setzt dirty
   *  bewusst zurueck - beim Blaettern wurde eine etwaige Dirty-Nachfrage bereits
   *  in pageTo() erledigt. */
  private loadEntry(id: string): void {
    this.notFound = false;

    if (id === NEW_ENTRY_PARAM) {
      // Interaktive Neuanlage: leerer Steckbrief, ID bleibt offen bis zum Save.
      this.isNew = true;
      this.entryId = '';
      this.draft = this.emptyEntry();
      this.original = this.deepClone(this.draft);
      this.dirty = false;
      this.recompute();
      this.loading = false;
      return;
    }

    this.isNew = false;
    this.entryId = id;
    const found = this.fullList.find(e => e?.id === id) || null;
    this.original = found;
    if (!found) {
      this.draft = null;
      this.notFound = true;
      this.loading = false;
      return;
    }
    this.draft = this.deepClone(found);
    this.migrateLegacyFields();
    this.dirty = false;
    this.recompute();
    this.loading = false;
  }

  /** Sortiert alle Steckbrief-IDs in Katalog-Reihenfolge (Seitenzahl auf-
   *  steigend; bei Gleichstand stabil nach HBF-Abkuerzung + Name). Leere IDs
   *  (z.B. noch nicht gespeicherte Neuanlagen) fallen raus. */
  private rebuildOrderedIds(): void {
    this.orderedIds = [...this.fullList]
      .sort((a, b) => this.compareCatalogOrder(a, b))
      .map(e => e?.id)
      .filter((id): id is string => !!id);
  }

  private compareCatalogOrder(a: BodensteckbriefEntry, b: BodensteckbriefEntry): number {
    const cmp = this.numCompare(parseInt(a?.seitenzahl ?? '', 10), parseInt(b?.seitenzahl ?? '', 10));
    if (cmp !== 0) return cmp;
    return (a?.hauptbodenform?.abkuerzung || '').localeCompare(b?.hauptbodenform?.abkuerzung || '', 'de')
      || (a?.hauptbodenform?.name || '').localeCompare(b?.hauptbodenform?.name || '', 'de');
  }

  /** Numerischer Vergleich; leere/ungueltige Werte landen hinter gueltigen
   *  (identische Logik wie im Katalog-Reader). */
  private numCompare(an: number, bn: number): number {
    const av = !Number.isNaN(an);
    const bv = !Number.isNaN(bn);
    if (av && bv) return an - bn;
    if (av) return -1;
    if (bv) return 1;
    return 0;
  }

  // ========== Vor/Zurueck-Blaettern (wie im Katalog-Viewer) ==========

  get position(): string {
    const i = this.orderedIds.indexOf(this.entryId);
    return i >= 0 ? `${i + 1} / ${this.orderedIds.length}` : '';
  }
  get hasPrev(): boolean {
    return this.orderedIds.indexOf(this.entryId) > 0;
  }
  get hasNext(): boolean {
    const i = this.orderedIds.indexOf(this.entryId);
    return i >= 0 && i < this.orderedIds.length - 1;
  }

  prev(): void {
    const i = this.orderedIds.indexOf(this.entryId);
    if (i > 0) this.pageTo(this.orderedIds[i - 1]);
  }
  next(): void {
    const i = this.orderedIds.indexOf(this.entryId);
    if (i >= 0 && i < this.orderedIds.length - 1) this.pageTo(this.orderedIds[i + 1]);
  }

  /** Blaettert zum Steckbrief <id>. Bei ungespeicherten Aenderungen erst nach-
   *  fragen (wie 'Zurueck'). Navigation per replaceUrl, damit Browser-Back zum
   *  urspruenglichen Aufrufer (Listen-Uebersicht) zurueckspringt und nicht durch
   *  alle durchgeblaetterten Steckbriefe hindurch. */
  private pageTo(id: string): void {
    if (this.saving) return;
    if (this.dirty) {
      this.confirmService
        .confirmDiscard('Es gibt ungespeicherte Änderungen. Wirklich verlassen?')
        .then(ok => { if (ok) this.navigateToEntry(id); });
      return;
    }
    this.navigateToEntry(id);
  }

  private navigateToEntry(id: string): void {
    this.router.navigate(['/listenverwaltung/steckbrief', id, 'edit'], { replaceUrl: true });
  }

  /** Komfort-Tastatur: Pfeiltasten blaettern - aber nur, wenn der Fokus NICHT in
   *  einem Eingabefeld liegt (sonst wuerde das Cursor-Bewegen im Text geklaut).
   *  Bei der Neuanlage ist Blaettern deaktiviert. */
  @HostListener('window:keydown', ['$event'])
  onKey(ev: KeyboardEvent): void {
    if (ev.defaultPrevented || this.isNew || this.loading || this.notFound) return;
    const t = ev.target as HTMLElement | null;
    if (t && (/^(INPUT|TEXTAREA|SELECT)$/i.test(t.tagName) || t.isContentEditable)) return;
    if (ev.key === 'ArrowLeft') { this.prev(); ev.preventDefault(); }
    else if (ev.key === 'ArrowRight') { this.next(); ev.preventDefault(); }
  }

  /** Minimaler, schema-gueltiger Leer-Steckbrief fuer die Neuanlage. Die ID
   *  bleibt leer und wird beim ersten Speichern aus makeNewEntryId() vergeben. */
  private emptyEntry(): BodensteckbriefEntry {
    return {
      id: '',
      hauptbodenform: {},
      spalten: {},
      feinbodenformen: [],
    };
  }

  /** Stabile, kollisionsfreie ID fuer manuell angelegte Steckbriefe. Echte
   *  D_Parser-IDs sind "<pdf>#<page>"; manuelle bekommen "manuell#<n>", wobei n
   *  ueber die bestehenden manuellen IDs hochgezaehlt wird (deterministisch,
   *  kein Date/Random -> testbar). */
  private makeNewEntryId(): string {
    let max = 0;
    for (const e of this.fullList) {
      const m = /^manuell#(\d+)$/.exec(e?.id || '');
      if (m) max = Math.max(max, Number(m[1]));
    }
    return `manuell#${max + 1}`;
  }

  /** Sammelt alle on-load-Migrationen alter D_Parser-Feldnamen in eine Stelle.
   *  Jede Sub-Methode ist tolerant gegen fehlende/leere Quellfelder und macht
   *  bewusst nichts schmutzig - der User soll nicht beim ersten Reinklicken
   *  einen "ungespeichert"-Hinweis sehen. */
  private migrateLegacyFields(): void {
    this.migrateHydromorphieToSection();
    this.migrateLegacyHorizontDiagramm();
    this.migrateExtraLeftSections();
    this.migrateHumushinweiseObject();
    this.migrateHumushinweiseLines();
    this.migrateLegacySingleSubstrat();
    this.sortSectionsHydromorphLast();
  }

  /** Sortiert draft.sections so, dass Hydromorphie-Sektionen ans Ende
   *  rutschen (= direkt oberhalb der Schraffur-Tabelle in der Preview).
   *  Stable: Reihenfolge innerhalb der beiden Gruppen bleibt erhalten. */
  private sortSectionsHydromorphLast(): void {
    if (!this.draft || !Array.isArray(this.draft.sections)) return;
    const isHydro = (s: BodensteckbriefSection) => /^hydromorph/i.test((s?.title || '').trim());
    const nonHydro = this.draft.sections.filter(s => !isHydro(s));
    const hydro = this.draft.sections.filter(s => isHydro(s));
    this.draft.sections = [...nonHydro, ...hydro];
  }

  /** D_Parser-Feld `horizont_diagramm` (Singular) → `horizont_diagramme[0]`
   *  (Plural, wie der Editor erwartet). Trifft alle Steckbriefe, in denen
   *  der Editor sonst das Hauptdiagramm gar nicht zeigt. */
  private migrateLegacyHorizontDiagramm(): void {
    if (!this.draft) return;
    const d = (this.draft as any).horizont_diagramm;
    if (!d || typeof d !== 'object') return;
    this.ensureDiagrammeArray();
    if (this.draft.horizont_diagramme!.length === 0) {
      this.draft.horizont_diagramme!.push({
        horizonte: Array.isArray(d.horizonte) ? d.horizonte : [],
        zonen: Array.isArray(d.zonen) ? d.zonen : [],
      });
    }
    delete (this.draft as any).horizont_diagramm;
  }

  /** D_Parser-Feld `extra_left_sections` → in `sections[]` mergen. Vermeidet
   *  Duplikate per Title-Vergleich (Trim + lowercase). */
  private migrateExtraLeftSections(): void {
    if (!this.draft) return;
    const arr = (this.draft as any).extra_left_sections;
    if (!Array.isArray(arr) || arr.length === 0) {
      if (Array.isArray(arr)) delete (this.draft as any).extra_left_sections;
      return;
    }
    this.ensureSections();
    const titleKey = (s: string) => (s || '').toLowerCase().trim();
    const existing = new Set(this.draft.sections!.map(s => titleKey(s?.title || '')));
    for (const sec of arr) {
      if (!sec || typeof sec !== 'object') continue;
      const key = titleKey(sec.title);
      if (existing.has(key)) continue;
      this.draft.sections!.push({
        kind: sec.kind || 'text',
        title: sec.title || '',
        content: sec.content || {},
      });
      existing.add(key);
    }
    delete (this.draft as any).extra_left_sections;
  }

  /** Haupt-Humus-Titel = EXAKT „Humus" oder „Humushinweise". Nummerierte
   *  Varianten („Humushinweise #2", „#3") sind eigenstaendige Sektionen (vom
   *  Parser bewusst getrennt) und duerfen die Haupt-Humushinweise NICHT
   *  aufsaugen - sonst verschwindet die Haupt-Sektion als eigener Block. */
  private isMainHumusTitle(title: string | undefined | null): boolean {
    return /^humus(?:hinweise)?$/i.test((title || '').trim());
  }

  /** D_Parser-Feld `humushinweise` (Objekt) → key_value-Sektion „Humushinweise".
   *  Falls schon eine Haupt-Humus-Sektion existiert, items dort einmergen. */
  private migrateHumushinweiseObject(): void {
    if (!this.draft) return;
    const obj = (this.draft as any).humushinweise;
    if (!obj || typeof obj !== 'object' || Array.isArray(obj) || Object.keys(obj).length === 0) {
      if (obj !== undefined) delete (this.draft as any).humushinweise;
      return;
    }
    this.ensureSections();
    const existing = this.draft.sections!.find(s => this.isMainHumusTitle(s?.title));
    if (existing) {
      existing.content = existing.content || {};
      existing.content.items = { ...(obj as Record<string, any>), ...(existing.content.items || {}) };
      // Falls die existierende Sektion noch 'text' war, auf 'key_value' heben,
      // sobald Items dazukommen - der Inhalt diktiert den Typ.
      if (existing.kind !== 'key_value') existing.kind = 'key_value';
    } else {
      this.draft.sections!.push({
        kind: 'key_value',
        title: 'Humushinweise',
        content: { items: { ...obj } },
      });
    }
    delete (this.draft as any).humushinweise;
  }

  /** D_Parser-Feld `humushinweise_lines` (Array) → Text-Zeilen in die
   *  Humus-Sektion. Falls keine existiert, neue Text-Sektion „Humushinweise". */
  private migrateHumushinweiseLines(): void {
    if (!this.draft) return;
    const arr = (this.draft as any).humushinweise_lines;
    if (!Array.isArray(arr) || arr.length === 0) {
      if (Array.isArray(arr)) delete (this.draft as any).humushinweise_lines;
      return;
    }
    const lines = arr.map((x: any) => String(x)).filter(x => x.length > 0);
    if (lines.length === 0) {
      delete (this.draft as any).humushinweise_lines;
      return;
    }
    this.ensureSections();
    const existing = this.draft.sections!.find(s => this.isMainHumusTitle(s?.title));
    if (existing) {
      const prev = Array.isArray(existing.content?.lines) ? existing.content.lines : [];
      existing.content = { ...(existing.content || {}), lines: [...prev, ...lines] };
    } else {
      this.draft.sections!.push({
        kind: 'text',
        title: 'Humushinweise',
        content: { lines },
      });
    }
    delete (this.draft as any).humushinweise_lines;
  }

  /** D_Parser-Feld `substrat` (Einzel-Objekt) → `substrat_layers[0]`, aber
   *  nur wenn substrat_layers leer ist (sonst kollidieren wir mit
   *  manuell editierten Layern). */
  private migrateLegacySingleSubstrat(): void {
    if (!this.draft) return;
    const obj = (this.draft as any).substrat;
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
      if (obj !== undefined) delete (this.draft as any).substrat;
      return;
    }
    if (!obj.kuerzel && !obj.beschreibung) {
      delete (this.draft as any).substrat;
      return;
    }
    if (!Array.isArray(this.draft.substrat_layers) || this.draft.substrat_layers.length === 0) {
      this.draft.substrat_layers = [{
        kuerzel: obj.kuerzel || '',
        beschreibung: obj.beschreibung || '',
      }];
    }
    delete (this.draft as any).substrat;
  }

  // ========== Lifecycle / Persistence ==========

  back(): void {
    if (this.dirty) {
      this.confirmService
        .confirmDiscard('Es gibt ungespeicherte Änderungen. Wirklich verlassen?')
        .then(ok => { if (ok) this.navigateBackToList(); });
      return;
    }
    this.navigateBackToList();
  }

  /** Zurueck in die Listenverwaltung - mit list/entry, damit die Liste wieder
   *  geoeffnet ist und auf den gerade bearbeiteten Steckbrief zentriert. */
  private navigateBackToList(): void {
    this.router.navigate(['/listenverwaltung'], {
      queryParams: { list: 'Bodensteckbriefe', entry: this.entryId || null },
    });
  }

  async save(): Promise<void> {
    if (!this.draft || this.saving) return;
    // Neuanlage: leeren Steckbrief nicht persistieren - mindestens HBF-Kuerzel
    // oder -Name verlangen, und die ID erst jetzt vergeben (Format manuell#<n>).
    if (this.isNew) {
      const hbf = this.draft.hauptbodenform || {};
      if (!(hbf.abkuerzung || '').trim() && !(hbf.name || '').trim()) {
        await this.confirmService.alert('Bitte mindestens eine HBF-Abkürzung oder einen Namen angeben.');
        return;
      }
      if (!this.draft.id) this.draft.id = this.makeNewEntryId();
    }
    // Vor dem Speichern Sektionen normalisieren: Hydromorphie ans Ende.
    // Waehrend der Bearbeitung bleibt die Reihenfolge stabil (damit si-
    // basierte Mutator-Calls nicht das falsche Section-Objekt treffen),
    // beim Save wird normalisiert und die naechste Open-Sitzung sieht
    // die korrekte Reihenfolge.
    this.sortSectionsHydromorphLast();
    this.saving = true;
    try {
      const idx = this.fullList.findIndex(e => e?.id === this.draft!.id);
      if (idx === -1) {
        // Neu - sollte nicht passieren, da wir per Edit-Route kommen, aber sicher ist sicher.
        this.fullList.push(this.draft);
      } else {
        this.fullList[idx] = this.draft;
      }
      await this.listenService.saveList('Bodensteckbriefe', this.fullList);
      await this.dataService.reloadCustomList('Bodensteckbriefe');
      this.original = this.deepClone(this.draft);
      // Ab jetzt ist der Eintrag persistiert: weitere Saves ersetzen ihn an
      // gleicher ID, und "Zurueck" zentriert die Liste auf den neuen Eintrag.
      this.entryId = this.draft.id;
      this.isNew = false;
      this.dirty = false;
      // Reihenfolge/Position neu berechnen: eine Neuanlage ist jetzt Teil der
      // Liste, und eine geaenderte Seitenzahl kann die Blaetter-Reihenfolge
      // verschieben.
      this.rebuildOrderedIds();
      await this.confirmService.alert('Steckbrief gespeichert.', 'Gespeichert');
    } catch (e: any) {
      console.error('Fehler beim Speichern:', e);
      const quota = (e?.name === 'QuotaExceededError') ||
                    /quota|exceeded|too\s*large/i.test(e?.message || '');
      const hint = quota
        ? '\n\nHinweis: Liste vermutlich zu gross für LocalStorage (~5 MB). DB-Verbindung nötig.'
        : '';
      await this.confirmService.alert('Fehler beim Speichern: ' + (e?.message || e) + hint);
    } finally {
      this.saving = false;
    }
  }

  discard(): void {
    if (!this.original) return;
    this.confirmService
      .confirmDiscard('Alle Änderungen verwerfen und Original neu laden?')
      .then(ok => {
        if (!ok) return;
        this.draft = this.deepClone(this.original!);
        this.recompute();
        this.dirty = false;
      });
  }

  /** Nach JEDER Mutation aufrufen. Setzt dirty + erneuert die Top-Level-
   *  Reference von `draft`, damit die eingebettete Live-Preview ngOnChanges
   *  bekommt und neu rendert. In-Place-Mutationen sind weiterhin OK fuer
   *  die Editor-eigene Default-CD - die Preview braucht aber den Ref-Wechsel.
   *  Sub-Mutations (Arrays/Objekte tiefer) werden geklont, wo sie veraendert
   *  werden, damit potenzielle OnPush-Konsumenten nicht stehenbleiben. */
  markDirty(): void {
    this.dirty = true;
    if (this.draft) this.draft = { ...this.draft };
  }

  togglePreview(): void { this.showPreview = !this.showPreview; }
  toggleOriginal(): void { this.showOriginal = !this.showOriginal; }
  get hasRightPane(): boolean { return this.showPreview || this.showOriginal; }

  /** Namensmatrix (Kürzel × Substrat) als Modal öffnen. Ein Klick auf eine
   *  Treffer-Zelle navigiert auf den zugehörigen Steckbrief (HBF-Seite). */
  openNamensmatrix(): void {
    this.namensmatrixService.open();
  }

  /** In den read-only Katalog-Viewer für diesen Steckbrief wechseln
   *  (Wechsel Editor→Viewer). Für noch nicht gespeicherte Neuanlagen (isNew)
   *  nicht verfügbar. Bei ungespeicherten Änderungen vorher nachfragen (wie
   *  back()), da der Viewer den gespeicherten Stand zeigt. */
  viewInReader(): void {
    const id = this.draft?.id;
    if (this.isNew || !id) return;
    const go = (): void => { void this.router.navigate(['/bodenformenkatalog', id]); };
    if (this.dirty) {
      this.confirmService
        .confirmDiscard('Es gibt ungespeicherte Änderungen. Trotzdem zur Ansicht wechseln?')
        .then(ok => { if (ok) go(); });
      return;
    }
    go();
  }

  // ========== Header (HBF) ==========

  setHbf(field: 'name' | 'abkuerzung' | 'substratgruppe', value: string): void {
    if (!this.draft) return;
    this.draft.hauptbodenform = this.draft.hauptbodenform || {};
    this.draft.hauptbodenform[field] = value;
    this.markDirty();
  }

  // ========== Header-Ebenen (Spalten-Labels) ==========

  /** Wert in einer Ebene einer Spalte aendern. */
  setEbene(col: string, levelIdx: number, value: string): void {
    if (!this.draft) return;
    const sp = (this.draft.spalten[col] = this.draft.spalten[col] || { ebenen: [] });
    sp.ebenen = [...(sp.ebenen || [])];
    while (sp.ebenen.length <= levelIdx) sp.ebenen.push('');
    sp.ebenen[levelIdx] = value;
    // trailing-leere am Ende rauskuerzen
    while (sp.ebenen.length && !sp.ebenen[sp.ebenen.length - 1].trim()) sp.ebenen.pop();
    this.markDirty();
    this.recompute();
  }

  // ========== Spalten-Aktionen (statt frueherer leer-Checkbox) ==========

  /** Spalten-Reihenfolge nach jeder expliziten Mutation persistieren, damit
   *  recompute() beim naechsten Aufruf nicht in die Heuristik zurueckfaellt. */
  private persistColOrder(): void {
    if (!this.draft) return;
    this.draft.schraf_col_order = [...this.schrafCols];
  }

  /** Spalte um eine Position nach links (-1) oder rechts (+1) verschieben. */
  moveCol(col: string, dir: -1 | 1): void {
    if (!this.draft) return;
    const i = this.schrafCols.indexOf(col);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= this.schrafCols.length) return;
    const next = [...this.schrafCols];
    [next[i], next[j]] = [next[j], next[i]];
    this.schrafCols = next;
    this.persistColOrder();
    this.markDirty();
  }

  /** Leere Spalte direkt vor/nach `col` einfuegen. */
  insertColAt(col: string, where: 'before' | 'after'): void {
    if (!this.draft) return;
    const i = this.schrafCols.indexOf(col);
    if (i < 0) return;
    const newId = this.makeNewColId();
    this.draft.spalten = this.draft.spalten || {};
    this.draft.spalten[newId] = { ebenen: [] };
    const insertAt = where === 'before' ? i : i + 1;
    const next = [...this.schrafCols];
    next.splice(insertAt, 0, newId);
    this.schrafCols = next;
    this.persistColOrder();
    this.markDirty();
  }

  /** Leere Spalte am Ende der Tabelle anhaengen (Button "+ Spalte"). */
  appendCol(): void {
    if (!this.draft) return;
    const newId = this.makeNewColId();
    this.draft.spalten = this.draft.spalten || {};
    this.draft.spalten[newId] = { ebenen: [] };
    this.schrafCols = [...this.schrafCols, newId];
    this.persistColOrder();
    this.markDirty();
  }

  /** Spalte aus der Anzeige entfernen + zugehoerige Schraffur-Werte in allen
   *  Feinbodenformen loeschen. Die Roh-Spalte in `spalten` bleibt erhalten,
   *  damit ggf. spaeter wieder einfuegbar (z.B. via Re-Import). */
  async removeCol(col: string): Promise<void> {
    if (!this.draft) return;
    const sp = this.draft.spalten?.[col];
    const label = (sp?.ebenen || []).filter(x => (x || '').trim()).join(' / ') || col;
    const ok = await this.confirmService.confirmDelete(`Spalte „${label}" wirklich löschen?`);
    if (!ok) return;
    this.schrafCols = this.schrafCols.filter(c => c !== col);
    this.persistColOrder();
    for (const f of (this.draft.feinbodenformen || [])) {
      if (f.schraffuren && f.schraffuren[col] !== undefined) {
        delete f.schraffuren[col];
      }
    }
    this.markDirty();
  }

  /** Nicht-numerische ID, damit die Heuristik in computeSchrafCols sie nicht
   *  mit Originalspalten verwechselt und Sortierung kollidiert. */
  private makeNewColId(): string {
    return 'c' + Math.random().toString(36).slice(2, 9);
  }

  // ========== Ebenen-Aktionen (pro Ebene-Zeile) ==========

  /** Ebene-Zeile um eine Position nach oben (-1) oder unten (+1) verschieben.
   *  Wird in jeder Schraffur-Spalte synchron als Index-Swap in `ebenen` umgesetzt. */
  moveEbeneRow(lvl: number, dir: -1 | 1): void {
    if (!this.draft) return;
    const j = lvl + dir;
    if (lvl < 0 || j < 0 || lvl >= this.nEbenen || j >= this.nEbenen) return;
    for (const col of this.schrafCols) {
      const sp = this.draft.spalten[col];
      if (!sp) continue;
      const e = sp.ebenen = [...(sp.ebenen || [])];
      while (e.length <= Math.max(lvl, j)) e.push('');
      [e[lvl], e[j]] = [e[j], e[lvl]];
      // trailing-leere zum Schluss wieder kuerzen, damit der Eintrag schlank bleibt
      while (e.length && !(e[e.length - 1] || '').trim()) e.pop();
    }
    this.markDirty();
  }

  /** Leere Ebene-Zeile direkt vor/nach Ebene `lvl` einfuegen. */
  insertEbeneRowAt(lvl: number, where: 'before' | 'after'): void {
    if (!this.draft) return;
    const insertAt = where === 'before' ? lvl : lvl + 1;
    for (const col of this.schrafCols) {
      const sp = this.draft.spalten[col];
      if (!sp) continue;
      const e = sp.ebenen = [...(sp.ebenen || [])];
      while (e.length < insertAt) e.push('');
      e.splice(insertAt, 0, '');
    }
    this.nEbenen += 1;
    this.markDirty();
  }

  /** Ebene-Zeile loeschen (in jeder Spalte den Index `lvl` aus `ebenen` schneiden). */
  removeEbeneRowAt(lvl: number): void {
    if (!this.draft) return;
    if (this.nEbenen <= 1) return;
    for (const col of this.schrafCols) {
      const sp = this.draft.spalten[col];
      if (!sp) continue;
      const e = sp.ebenen = [...(sp.ebenen || [])];
      if (e.length > lvl) e.splice(lvl, 1);
      while (e.length && !(e[e.length - 1] || '').trim()) e.pop();
    }
    this.nEbenen -= 1;
    this.markDirty();
  }

  // ========== FBF-Rows ==========

  setFbfField(idx: number, field: keyof BodensteckbriefFbfRow, value: any): void {
    if (!this.draft) return;
    const f = this.draft.feinbodenformen?.[idx];
    if (!f) return;
    (f as any)[field] = value;
    this.markDirty();
    if (field === 'footnote_ref') this.hasFootnoteCol = (this.draft.feinbodenformen || []).some(x => !!x.footnote_ref);
  }

  toggleStruck(idx: number, value: boolean): void {
    this.setFbfField(idx, 'gestrichen', value);
  }

  addFbfRow(): void {
    if (!this.draft) return;
    const name = window.prompt('Lokalname der neuen Feinbodenform:');
    if (!name || !name.trim()) return;
    this.draft.feinbodenformen = this.draft.feinbodenformen || [];
    this.draft.feinbodenformen.push({
      lokalname: name.trim(),
      abkuerzung: '',
      gestrichen: false,
      schraffuren: {},
    });
    this.markDirty();
  }

  /** Zeile um eine Position nach oben (-1) oder unten (+1) verschieben. */
  moveFbf(idx: number, dir: -1 | 1): void {
    if (!this.draft) return;
    const arr = this.draft.feinbodenformen || [];
    const j = idx + dir;
    if (idx < 0 || j < 0 || idx >= arr.length || j >= arr.length) return;
    const next = [...arr];
    [next[idx], next[j]] = [next[j], next[idx]];
    this.draft.feinbodenformen = next;
    this.markDirty();
  }

  /** Leere FBF-Zeile direkt vor/nach Index `idx` einfuegen. */
  insertFbfAt(idx: number, where: 'before' | 'after'): void {
    if (!this.draft) return;
    this.draft.feinbodenformen = this.draft.feinbodenformen || [];
    const insertAt = where === 'before' ? idx : idx + 1;
    this.draft.feinbodenformen.splice(insertAt, 0, {
      lokalname: '',
      abkuerzung: '',
      gestrichen: false,
      schraffuren: {},
    });
    this.markDirty();
  }

  async removeFbfRow(idx: number): Promise<void> {
    if (!this.draft) return;
    const f = this.draft.feinbodenformen?.[idx];
    if (!f) return;
    const ok = await this.confirmService.confirmDelete(`"${f.lokalname}" wirklich löschen?`);
    if (!ok) return;
    this.draft.feinbodenformen.splice(idx, 1);
    this.markDirty();
  }

  // ========== Schraffur-Cells ==========

  /** Aktueller Schraffur-Zustand einer Zelle. Liest schraffuren zuerst,
   *  faellt auf raw_fills zurueck (=Original-D_Parser-Grid). */
  cellState(f: BodensteckbriefFbfRow, col: string): string {
    const direct = f.schraffuren?.[col];
    if (direct !== undefined && direct !== null) return direct;
    const fills = (this.draft?.raw_fills || {})[f.lokalname] || {};
    const v = fills[col];
    if (!v) return '';
    if (v === 'schr') return 'haken';
    const up = String(v).toUpperCase();
    if (up === 'X') return 'x';
    if (v === '?') return '?';
    return String(v);
  }

  /** 4er-Zyklus: leer → haken → x → ? → leer. Freitext-Zellen oeffnen direkt
   *  den Text-Editor (Doppelklick-Verhalten dann redundant, aber kein Konflikt). */
  cycleSchraffur(idx: number, col: string): void {
    if (!this.draft) return;
    const f = this.draft.feinbodenformen[idx];
    if (!f) return;
    const cur = this.cellState(f, col);
    if (!SCHRAF_CYCLE.has(cur)) {
      this.editSchraffurText(idx, col);
      return;
    }
    const next = SCHRAF_NEXT[cur] ?? 'haken';
    f.schraffuren = { ...(f.schraffuren || {}) };
    if (next === '') delete f.schraffuren[col];
    else f.schraffuren[col] = next;
    this.markDirty();
  }

  /** Freitext fuer eine Schraffur-Zelle setzen (z.B. roem. Ziffer "II, IV"). */
  editSchraffurText(idx: number, col: string): void {
    if (!this.draft) return;
    const f = this.draft.feinbodenformen[idx];
    if (!f) return;
    const cur = this.cellState(f, col);
    const initial = SCHRAF_CYCLE.has(cur) ? '' : cur;
    const next = window.prompt(
      'Zellinhalt:\n• leer = Zelle leeren\n• "haken"/"x"/"?" = Standard-Marker\n• sonst Freitext',
      initial,
    );
    if (next === null) return;
    f.schraffuren = { ...(f.schraffuren || {}) };
    const trimmed = next.trim();
    if (trimmed === '') delete f.schraffuren[col];
    else f.schraffuren[col] = trimmed;
    this.markDirty();
  }

  /** CSS-Klasse fuer eine Schraffur-Zelle anhand des Zustands. */
  cellClass(state: string): string {
    if (state === 'haken') return 'sch-cell sch-haken';
    if (state === 'x' || state === '?') return 'sch-cell sch-marker';
    if (state) return 'sch-cell sch-text';
    return 'sch-cell sch-empty';
  }
  cellText(state: string): string {
    if (state === 'haken') return '';
    if (state === 'x') return 'X';
    if (state === '?') return '?';
    return state || '';
  }

  // ========== Spaltenfilter & Recompute ==========

  /** Schraffur-Spalten ermitteln. Bevorzugt explizite `schraf_col_order` (vom
   *  User per Move/Insert/Delete gesetzt). Fallback: Heuristik wie in der
   *  Preview - sortiert nach numerischer ID, filtert Fix-/Daten-Only-Spalten. */
  private recompute(): void {
    if (!this.draft) return;
    const spalten = this.draft.spalten || {};
    const fbfs = this.draft.feinbodenformen || [];
    const rawFills = this.draft.raw_fills || {};

    if (Array.isArray(this.draft.schraf_col_order) && this.draft.schraf_col_order.length) {
      this.schrafCols = this.draft.schraf_col_order.filter(c => !!spalten[c]);
    } else {
      const ids = Object.keys(spalten).filter(c => /^-?\d+$/.test(c)).sort((a, b) => Number(a) - Number(b));
      const isNonSchrafHeader = (sp: BodensteckbriefSpalte) => {
        const t = (sp?.ebenen || []).map(x => String(x || '').toLowerCase()).join(' ');
        return NON_SCHRAF_TOKENS.some(tok => t.includes(tok));
      };
      this.schrafCols = ids.filter(col => {
        const sp = spalten[col];
        if (!sp) return false;
        if (sp.empty) return false;
        const lv = sp.ebenen || [];
        if (!lv.some(x => String(x || '').trim())) return false;
        if (isNonSchrafHeader(sp)) return false;
        let hasSchraf = false, hasDataText = false;
        for (const f of fbfs) {
          const fills = rawFills[f.lokalname] || {};
          const v = fills[col];
          const sch = f.schraffuren?.[col];
          if (sch === 'haken' || v === 'schr') hasSchraf = true;
          else if (v) {
            const s = String(v).trim();
            if (s.length > 2 || ['M','Z','A','K','R'].includes(s.toUpperCase())) hasDataText = true;
          }
        }
        if (hasDataText && !hasSchraf) return false;
        return true;
      });
    }
    // nEbenen mind. Maximum aller Spalten-Ebenen, mind. 2.
    let max = 2;
    for (const c of this.schrafCols) max = Math.max(max, (spalten[c]?.ebenen || []).length);
    this.nEbenen = Math.max(this.nEbenen, max);
    this.hasFootnoteCol = fbfs.some(f => !!f.footnote_ref);
  }

  // ========== Helpers ==========

  range(n: number): number[] {
    const len = Math.max(0, Math.floor(n || 0));
    return Array.from({ length: len }, (_v, i) => i);
  }

  ebeneValue(col: string, lvl: number): string {
    return this.draft?.spalten?.[col]?.ebenen?.[lvl] || '';
  }

  private deepClone<T>(x: T): T {
    return JSON.parse(JSON.stringify(x));
  }

  // ========== Substrat-Layer ==========

  /** Liefert die Layers (immer mind. ein Eintrag, damit die UI was rendern kann). */
  get substratLayersDraft(): Array<{ kuerzel: string; beschreibung: string }> {
    if (!this.draft) return [];
    return this.draft.substrat_layers && this.draft.substrat_layers.length
      ? this.draft.substrat_layers.map(l => ({ kuerzel: l?.kuerzel || '', beschreibung: l?.beschreibung || '' }))
      : [{ kuerzel: '', beschreibung: '' }];
  }

  private ensureSubstratLayers(): void {
    if (!this.draft) return;
    if (!Array.isArray(this.draft.substrat_layers) || !this.draft.substrat_layers.length) {
      this.draft.substrat_layers = [{ kuerzel: '', beschreibung: '' }];
    }
  }

  setSubstratLayer(i: number, field: 'kuerzel' | 'beschreibung', value: string): void {
    if (!this.draft) return;
    this.ensureSubstratLayers();
    const layers = this.draft.substrat_layers!;
    if (i < 0 || i >= layers.length) return;
    layers[i] = { ...layers[i], [field]: value };
    this.markDirty();
  }
  addSubstratLayer(): void {
    if (!this.draft) return;
    this.ensureSubstratLayers();
    this.draft.substrat_layers!.push({ kuerzel: '', beschreibung: '' });
    this.markDirty();
  }
  removeSubstratLayer(i: number): void {
    if (!this.draft?.substrat_layers) return;
    if (i < 0 || i >= this.draft.substrat_layers.length) return;
    this.draft.substrat_layers.splice(i, 1);
    if (this.draft.substrat_layers.length === 0) {
      delete this.draft.substrat_layers;
    }
    this.markDirty();
  }

  // ========== Horizont-Diagramme ==========

  /** Liefert die Diagramme als Array (auch wenn entry.horizont single-form ist). */
  get diagrammeDraft(): Array<{ horizonte: any[]; zonen: any[] }> {
    if (!this.draft) return [];
    if (Array.isArray(this.draft.horizont_diagramme) && this.draft.horizont_diagramme.length) {
      return this.draft.horizont_diagramme;
    }
    if (this.draft.horizont && (this.draft.horizont.horizonte || this.draft.horizont.zonen)) {
      return [{ horizonte: this.draft.horizont.horizonte || [], zonen: this.draft.horizont.zonen || [] }];
    }
    return [];
  }

  /** Normalisiert auf horizont_diagramme-Array (legacy entry.horizont wird konvertiert). */
  private ensureDiagrammeArray(): void {
    if (!this.draft) return;
    if (!Array.isArray(this.draft.horizont_diagramme)) {
      const legacy = this.draft.horizont;
      this.draft.horizont_diagramme = legacy && (legacy.horizonte || legacy.zonen)
        ? [{ horizonte: legacy.horizonte || [], zonen: legacy.zonen || [] }]
        : [];
      delete this.draft.horizont;
    }
  }

  /** Max 4 Diagramme erlauben (Layout: 2x2). */
  static readonly MAX_DIAGRAMME = 4;

  get canAddDiagramm(): boolean {
    const n = this.diagrammeDraft.length;
    return n < BodensteckbriefEditorComponent.MAX_DIAGRAMME;
  }

  addDiagramm(): void {
    if (!this.draft) return;
    if (!this.canAddDiagramm) return;
    this.ensureDiagrammeArray();
    this.draft.horizont_diagramme!.push({ horizonte: [], zonen: [] });
    this.markDirty();
  }

  // ========== Griechische-Buchstaben-Palette fuer Zonen-Eingabe ==========

  /** Haeufige griechische Buchstaben fuer Zonen-Codes. */
  readonly GREEK_LETTERS = ['α','β','γ','δ','ε','ζ','η','θ','ι','κ','λ','μ','ν','ξ','π','ρ','σ','τ','φ','χ','ψ','ω'];

  /** Welches Zonen-Feld zuletzt im Focus war - die Palette appended dort. */
  activeZoneEdit: { diagIdx: number; rowIdx: number } | null = null;

  onZoneFocus(diagIdx: number, rowIdx: number): void {
    this.activeZoneEdit = { diagIdx, rowIdx };
  }

  insertGreekIntoZone(letter: string): void {
    if (!this.draft || !this.activeZoneEdit) return;
    const { diagIdx, rowIdx } = this.activeZoneEdit;
    const d = (this.draft.horizont_diagramme || [])[diagIdx];
    if (!d) return;
    const cur = this.symOf((d.zonen || [])[rowIdx]);
    this.setDiagrammCell(diagIdx, rowIdx, 'zonen', cur + letter);
  }
  removeDiagramm(idx: number): void {
    if (!this.draft) return;
    this.ensureDiagrammeArray();
    const arr = this.draft.horizont_diagramme!;
    if (idx < 0 || idx >= arr.length) return;
    arr.splice(idx, 1);
    this.markDirty();
  }
  /** Eine Zeile in einem Diagramm = ein (horizont, zone)-Paar. */
  addDiagrammRow(diagIdx: number): void {
    if (!this.draft) return;
    this.ensureDiagrammeArray();
    const d = this.draft.horizont_diagramme![diagIdx];
    if (!d) return;
    d.horizonte = [...(d.horizonte || []), ''];
    d.zonen = [...(d.zonen || []), ''];
    this.markDirty();
  }
  /** Eine einzelne Spalten-Zelle (NUR Horizont ODER NUR Zone) um -1/+1
   *  innerhalb DIESER Spalte verschieben. Die andere Spalte bleibt unberuehrt -
   *  Horizont- und Zonen-Spalte sind bewusst unabhaengig voneinander. */
  moveDiagrammCell(diagIdx: number, kind: 'horizonte' | 'zonen', idx: number, dir: -1 | 1): void {
    if (!this.draft) return;
    this.ensureDiagrammeArray();
    const d = this.draft.horizont_diagramme![diagIdx];
    if (!d) return;
    const arr = ((d as any)[kind] = [...((d as any)[kind] || [])]);
    const j = idx + dir;
    if (idx < 0 || j < 0 || idx >= arr.length || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    this.markDirty();
  }
  /** Eine einzelne Spalten-Zelle herausschneiden: nur aus DIESER Spalte
   *  entfernen, alles darunter rueckt eine Position hoch. Die andere Spalte
   *  bleibt unveraendert - die Index-Ausrichtung wird bewusst entkoppelt. */
  removeDiagrammCell(diagIdx: number, kind: 'horizonte' | 'zonen', idx: number): void {
    if (!this.draft) return;
    this.ensureDiagrammeArray();
    const d = this.draft.horizont_diagramme![diagIdx];
    if (!d) return;
    const arr = ((d as any)[kind] = [...((d as any)[kind] || [])]);
    if (idx < 0 || idx >= arr.length) return;
    arr.splice(idx, 1);
    // Fokus-Marker der Griechen-Palette invalidieren, wenn die fokussierte
    // Zonen-Zelle entfernt wurde oder durch das Nachruecken verrutscht.
    if (kind === 'zonen' && this.activeZoneEdit?.diagIdx === diagIdx && this.activeZoneEdit.rowIdx >= idx) {
      this.activeZoneEdit = null;
    }
    this.markDirty();
  }
  /** Anzahl real befuellter Zellen einer Spalte. Damit blendet das Template die
   *  Zellen-Aktionen (Verschieben/Loeschen) nur an existierenden Zellen ein -
   *  die virtuellen Leerzeilen unterhalb der kuerzeren Spalte bekommen keine. */
  colLen(diag: { horizonte: any[]; zonen: any[] }, kind: 'horizonte' | 'zonen'): number {
    return (((diag as any)?.[kind]) || []).length;
  }
  setDiagrammCell(diagIdx: number, rowIdx: number, kind: 'horizonte' | 'zonen', value: string): void {
    if (!this.draft) return;
    this.ensureDiagrammeArray();
    const d = this.draft.horizont_diagramme![diagIdx];
    if (!d) return;
    const arr = (d as any)[kind] = [...((d as any)[kind] || [])];
    while (arr.length <= rowIdx) arr.push('');
    arr[rowIdx] = value;
    this.markDirty();
  }
  /** Helper fuer Template: Symbol-Text aus String oder {symbol,note}. */
  symOf(x: any): string {
    if (!x) return '';
    if (typeof x === 'string') return x;
    return x.symbol || x.name || x.text || '';
  }
  diagrammRowCount(d: { horizonte: any[]; zonen: any[] }): number {
    return Math.max((d?.horizonte || []).length, (d?.zonen || []).length);
  }

  // ========== Sektionen ==========

  get sectionsDraft(): BodensteckbriefSection[] {
    if (!this.draft) return [];
    return this.draft.sections || [];
  }

  private ensureSections(): void {
    if (!this.draft) return;
    if (!Array.isArray(this.draft.sections)) this.draft.sections = [];
  }

  addSection(): void {
    if (!this.draft) return;
    const title = window.prompt('Titel der neuen Sektion (z.B. „Humushinweise"):');
    if (!title || !title.trim()) return;
    const kind = window.prompt('Typ (key_value / text / list):', 'text');
    if (!kind) return;
    this.ensureSections();
    const content = kind === 'key_value' ? { items: {} as Record<string, string> }
                  : kind === 'list' ? { items: [] as string[] }
                  : { lines: [] as string[] };
    this.draft.sections!.push({ kind: kind.trim(), title: title.trim(), content });
    this.markDirty();
  }
  async removeSection(i: number): Promise<void> {
    if (!this.draft?.sections) return;
    const sec = this.draft.sections[i];
    if (!sec) return;
    const ok = await this.confirmService.confirmDelete(`Sektion „${sec.title}" entfernen?`);
    if (!ok) return;
    this.draft.sections.splice(i, 1);
    this.markDirty();
  }
  setSectionTitle(i: number, value: string): void {
    if (!this.draft?.sections?.[i]) return;
    this.draft.sections[i] = { ...this.draft.sections[i], title: value };
    this.markDirty();
  }
  setSectionKind(i: number, value: string): void {
    if (!this.draft?.sections?.[i]) return;
    const sec = this.draft.sections[i];
    let content: any;
    if (value === 'key_value') content = { items: {} };
    else if (value === 'list') content = { items: [] };
    else content = { lines: [] };
    this.draft.sections[i] = { ...sec, kind: value, content };
    this.markDirty();
  }
  /** key_value: Items als Array<[key,value]> fuer einfache Template-Iteration. */
  kvItems(sec: BodensteckbriefSection): Array<{ key: string; value: string }> {
    const items = sec?.content?.items;
    if (!items || typeof items !== 'object' || Array.isArray(items)) return [];
    return Object.entries(items).map(([k, v]) => ({ key: k, value: String(v) }));
  }
  addKvItem(sectionIdx: number): void {
    if (!this.draft?.sections?.[sectionIdx]) return;
    const sec = this.draft.sections[sectionIdx];
    const key = window.prompt('Schlüssel:');
    if (!key || !key.trim()) return;
    const items = { ...(sec.content?.items || {}) };
    items[key.trim()] = '';
    this.draft.sections[sectionIdx] = { ...sec, content: { ...sec.content, items } };
    this.markDirty();
  }
  updateKvValue(sectionIdx: number, key: string, value: string): void {
    if (!this.draft?.sections?.[sectionIdx]) return;
    const sec = this.draft.sections[sectionIdx];
    const items = { ...(sec.content?.items || {}) };
    items[key] = value;
    this.draft.sections[sectionIdx] = { ...sec, content: { ...sec.content, items } };
    this.markDirty();
  }
  removeKvItem(sectionIdx: number, key: string): void {
    if (!this.draft?.sections?.[sectionIdx]) return;
    const sec = this.draft.sections[sectionIdx];
    const items = { ...(sec.content?.items || {}) };
    delete items[key];
    this.draft.sections[sectionIdx] = { ...sec, content: { ...sec.content, items } };
    this.markDirty();
  }
  /** Schluessel eines key_value-Eintrags umbenennen (z.B. „O-Horizont" →
   *  „O-Lage"). Analog zu renameFussnoteMarker: Reihenfolge bleibt erhalten,
   *  Kollision mit bestehendem Schluessel wird abgefangen (sonst stiller
   *  Datenverlust). No-op bei leerem/unveraendertem Schluessel. */
  renameKvKey(sectionIdx: number, oldKey: string, newKey: string): void {
    if (!this.draft?.sections?.[sectionIdx]) return;
    const next = (newKey || '').trim();
    if (!next || next === oldKey) return;
    const sec = this.draft.sections[sectionIdx];
    const items = { ...(sec.content?.items || {}) };
    if (Object.prototype.hasOwnProperty.call(items, next)) {
      void this.confirmService.alert(`Schlüssel „${next}" ist bereits vergeben. Bitte einen anderen wählen oder den Konflikt zuerst auflösen.`);
      return;
    }
    // Neu aufbauen, damit der umbenannte Key an derselben Stelle bleibt.
    const rebuilt: Record<string, any> = {};
    for (const [k, v] of Object.entries(items)) {
      if (k === oldKey) rebuilt[next] = v;
      else rebuilt[k] = v;
    }
    this.draft.sections[sectionIdx] = { ...sec, content: { ...sec.content, items: rebuilt } };
    this.markDirty();
  }
  /** text: Lines als Mehrzeilen-Textarea (durch \n getrennt). */
  textLines(sec: BodensteckbriefSection): string {
    const lines = sec?.content?.lines;
    return Array.isArray(lines) ? lines.join('\n') : '';
  }
  setTextLines(sectionIdx: number, raw: string): void {
    if (!this.draft?.sections?.[sectionIdx]) return;
    const sec = this.draft.sections[sectionIdx];
    const lines = (raw || '').split(/\r?\n/).filter(x => x !== undefined);
    this.draft.sections[sectionIdx] = { ...sec, content: { ...sec.content, lines } };
    this.markDirty();
  }
  /** list: Items als String-Array (multiline-Textarea). */
  listItemsText(sec: BodensteckbriefSection): string {
    const items = sec?.content?.items;
    return Array.isArray(items) ? items.map((x: any) => String(x)).join('\n') : '';
  }
  setListItems(sectionIdx: number, raw: string): void {
    if (!this.draft?.sections?.[sectionIdx]) return;
    const sec = this.draft.sections[sectionIdx];
    const items = (raw || '').split(/\r?\n/).filter(x => x.length > 0);
    this.draft.sections[sectionIdx] = { ...sec, content: { ...sec.content, items } };
    this.markDirty();
  }

  // ========== Hydromorphie-Sektion (Sonderfall in draft.sections[]) ==========

  /** Test: title startet mit "hydromorph" (case-insensitive). Gleicher Filter
   *  wie die Preview-classifySections - dadurch landet derselbe Section-Eintrag
   *  hier im inline-Editor ueber der Tabelle (statt in der generischen
   *  Sektionen-Card weiter unten). */
  private isHydromorphieTitle(title: string | undefined | null): boolean {
    return /^hydromorph/i.test((title || '').trim());
  }

  /** Liefert die existierende Hydromorphie-Sektion, ohne sie anzulegen. */
  private findHydromorphieSection(): BodensteckbriefSection | null {
    const list = this.draft?.sections;
    if (!Array.isArray(list)) return null;
    return list.find(s => this.isHydromorphieTitle(s?.title)) || null;
  }

  /** Hydromorphie-Sektion finden oder neu anlegen (kind: 'text' mit Lines). */
  private findOrCreateHydromorphieSection(): BodensteckbriefSection {
    this.ensureSections();
    let sec = this.findHydromorphieSection();
    if (!sec) {
      sec = { kind: 'text', title: 'Hydromorphiehinweise', content: { lines: [] as string[] } };
      this.draft!.sections!.push(sec);
    }
    return sec;
  }

  /** One-shot-Migration auf Load: falls draft noch im Legacy-Format
   *  (entry.hydromorphiehinweise: string[]) vorliegt und es noch keine
   *  Hydromorphie-Sektion gibt, daraus eine Sektion bauen und das Top-Level-
   *  Feld entfernen. Wird in ngOnInit aufgerufen, *bevor* dirty gesetzt wird,
   *  damit der reine Load nicht ueberraschend als ungespeichert gilt - der
   *  Save passiert erst, wenn der User wirklich was aendert. */
  private migrateHydromorphieToSection(): void {
    if (!this.draft) return;
    const arr = (this.draft as any).hydromorphiehinweise;
    if (!Array.isArray(arr) || arr.length === 0) return;
    if (this.findHydromorphieSection()) {
      // Sektion existiert bereits - Top-Level ist redundant, entfernen.
      delete (this.draft as any).hydromorphiehinweise;
      return;
    }
    this.ensureSections();
    this.draft.sections!.push({
      kind: 'text',
      title: 'Hydromorphiehinweise',
      content: { lines: arr.map((x: any) => String(x)).filter((x: string) => x.length > 0) },
    });
    delete (this.draft as any).hydromorphiehinweise;
  }

  // ========== Rohdaten / Unbekannte Top-Level-Felder ==========

  /** Felder, die der Editor schon mit dediziertem UI abdeckt ODER auf Load
   *  in andere Strukturen migriert. Alles andere am Top-Level wird in der
   *  "Sonstige Felder"-Card als JSON gezeigt, damit nichts aus dem Parser
   *  stillschweigend verloren geht. */
  private readonly HANDLED_TOP_LEVEL = new Set<string>([
    'id', 'pdf', 'page',
    'hauptbodenform', 'spalten', 'feinbodenformen', 'schraf_col_order',
    'sections',
    'substrat_layers', 'substrat_kuerzel',
    'horizont_diagramme', 'horizont',
    'fussnoten',
    'klassifikation', 'seitenzahl',
    'fbf_klassensuffix',
    'raw_fills',
    // Edit/Save-Metadaten vom D_Parser-UI - unangetastet behalten, kein UI noetig.
    '_provenance',
    // Werden in migrateLegacyFields() auf Load in andere Strukturen migriert:
    'hydromorphiehinweise',  // -> Sektion (Title hydromorph*)
    'horizont_diagramm',     // -> horizont_diagramme[0]
    'extra_left_sections',   // -> sections[]
    'humushinweise',         // -> Sektion key_value (Title 'Humushinweise')
    'humushinweise_lines',   // -> Sektion text (Title 'Humushinweise')
    'substrat',              // -> substrat_layers[0]
  ]);

  /** Liste der nicht im Editor abgedeckten Top-Level-Felder mit ihrem JSON-
   *  Wert. UI ist read-only - damit der User sieht, was im Datensatz steckt
   *  und mir bei Bedarf sagen kann, wo es UI-seitig hingehoeren soll. */
  get unhandledFields(): Array<{ key: string; value: string }> {
    if (!this.draft) return [];
    const out: Array<{ key: string; value: string }> = [];
    for (const [k, v] of Object.entries(this.draft)) {
      if (this.HANDLED_TOP_LEVEL.has(k)) continue;
      if (v === undefined || v === null) continue;
      // leere Container-Werte unterdruecken - sind nicht spannend
      if (Array.isArray(v) && v.length === 0) continue;
      if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) continue;
      let s: string;
      try { s = typeof v === 'string' ? v : JSON.stringify(v, null, 2); }
      catch { s = String(v); }
      out.push({ key: k, value: s });
    }
    return out.sort((a, b) => a.key.localeCompare(b.key));
  }

  // ========== Fussnoten ==========

  /** Items aus fussnoten als (marker, text)-Liste fuer das Template. */
  get fussnotenItems(): Array<{ key: string; value: string }> {
    const f = this.draft?.fussnoten;
    if (!f) return [];
    const items = f.items;
    if (!items || typeof items !== 'object' || Array.isArray(items)) return [];
    return Object.entries(items).map(([k, v]) => ({ key: k, value: String(v) }));
  }
  get fussnotenLinesText(): string {
    const f = this.draft?.fussnoten;
    const lines = f?.lines;
    if (Array.isArray(lines)) return lines.join('\n');
    // alternativ kann fussnoten.items auch eine Liste sein
    if (Array.isArray(f?.items)) return (f!.items as any[]).map(x => String(x)).join('\n');
    return '';
  }
  private ensureFussnoten(): void {
    if (!this.draft) return;
    if (!this.draft.fussnoten || typeof this.draft.fussnoten !== 'object') this.draft.fussnoten = {};
    if (!this.draft.fussnoten.items || typeof this.draft.fussnoten.items !== 'object' || Array.isArray(this.draft.fussnoten.items)) {
      this.draft.fussnoten.items = {};
    }
  }
  addFussnote(): void {
    if (!this.draft) return;
    const marker = window.prompt('Marker (z.B. „1)" oder „²"):');
    if (!marker || !marker.trim()) return;
    this.ensureFussnoten();
    this.draft.fussnoten.items[marker.trim()] = '';
    this.markDirty();
  }
  updateFussnote(marker: string, value: string): void {
    if (!this.draft) return;
    this.ensureFussnoten();
    this.draft.fussnoten.items[marker] = value;
    this.markDirty();
  }

  /** Marker (key) einer Fussnote umbenennen. Das ist ein "rename in object":
   *  alten Key loeschen, neuen Key mit demselben Wert einfuegen. Wenn der
   *  neue Key schon existiert, wird nicht ueberschrieben (sonst Datenverlust). */
  renameFussnoteMarker(oldMarker: string, newMarker: string): void {
    if (!this.draft) return;
    const next = (newMarker || '').trim();
    if (!next || next === oldMarker) return;
    this.ensureFussnoten();
    const items = this.draft.fussnoten.items as Record<string, string>;
    if (Object.prototype.hasOwnProperty.call(items, next)) {
      // Kollision - User entscheiden lassen (waere sonst stiller Datenverlust).
      void this.confirmService.alert(`Marker „${next}" ist bereits vergeben. Bitte einen anderen wählen oder den Konflikt zuerst auflösen.`);
      return;
    }
    // Reihenfolge der Items erhalten: neu aufbauen mit umbenanntem Key an
    // der gleichen Stelle.
    const value = items[oldMarker] ?? '';
    const rebuilt: Record<string, string> = {};
    for (const [k, v] of Object.entries(items)) {
      if (k === oldMarker) rebuilt[next] = value;
      else rebuilt[k] = v;
    }
    this.draft.fussnoten.items = rebuilt;
    this.markDirty();
  }
  removeFussnote(marker: string): void {
    if (!this.draft?.fussnoten?.items) return;
    delete this.draft.fussnoten.items[marker];
    this.markDirty();
  }
  setFussnotenLines(raw: string): void {
    if (!this.draft) return;
    if (!this.draft.fussnoten || typeof this.draft.fussnoten !== 'object') this.draft.fussnoten = {};
    this.draft.fussnoten.lines = (raw || '').split(/\r?\n/).filter(x => x.length > 0);
    this.markDirty();
  }
}
